/**
 * Host-side calibrated latency probe: NTP-style offset handshake + report correlation.
 *
 * Extracted from PeerConnectionManager so ping cadence, offset tracking, and calibrated
 * sink logging stay testable without the full peer manager. Reports are keyed on media
 * RTP timestamp; every sample records an explicit `source` (no silent fallback).
 * `estimate` sources are accepted for the debug log only — never feed the climb gate.
 */

import {
  CalibratedLatencyTracker,
  LatencyOffsetTracker,
  LATENCY_PROBE_MESSAGE_TYPE,
  type CalibratedLatencySummary,
  type LatencyProbePong,
  type LatencyProbeReport,
  type LatencyProbeSource,
} from '@playcastdotio/PeerCore';

export type LatencyProbeManagerDeps = {
  send: (peerId: string, message: unknown) => boolean;
  nowMs?: () => number;
  monotonicNowMs?: () => number;
  debugSink?: (line: string) => void;
  /** Hard cap on pings per peer while waiting for a trusted offset. Default 40. */
  maxPingsPerWindow?: number;
  /** Resolve host encode wall time for a media RTP timestamp (insertable-streams meter). */
  getHostSendWallMs?: (peerId: string, rtpTimestamp: number) => number | undefined;
  /**
   * Fired for every trusted calibrated sample (never for `estimate` reports). The guest
   * HUD's signal bars are published from this — the player-visible latency.
   */
  onCalibratedSample?: (peerId: string, calibratedMs: number) => void;
};

const DEFAULT_MAX_PINGS = 40;
const PING_INTERVAL_MS = 250;
const CALIBRATION_RENEW_MS = 45_000;

export class LatencyProbeManager {
  private readonly nowMs: () => number;
  private readonly monotonicNowMs: () => number;
  private readonly maxPings: number;
  private readonly offsetTrackers = new Map<string, LatencyOffsetTracker>();
  private readonly calibratedTrackers = new Map<string, CalibratedLatencyTracker>();
  private readonly sourceByPeer = new Map<string, LatencyProbeSource>();
  private readonly seqByPeer = new Map<string, number>();
  private readonly sentAt = new Map<string, Map<number, number>>();
  private readonly timers = new Map<string, ReturnType<typeof setInterval>>();
  private readonly pingCount = new Map<string, number>();
  private readonly epochByPeer = new Map<string, number>();
  private readonly renewals = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly windowStartedAt = new Map<string, number>();
  private readonly activePeers = new Set<string>();
  private readonly clockReadings = new Map<string, { wallMs: number; monotonicMs: number }>();
  private readonly invalidCurrent = new Set<string>();

  constructor(private readonly deps: LatencyProbeManagerDeps) {
    this.nowMs = deps.nowMs ?? (() => Date.now());
    this.monotonicNowMs = deps.monotonicNowMs ?? deps.nowMs ?? (() => performance.now());
    this.maxPings = deps.maxPingsPerWindow ?? DEFAULT_MAX_PINGS;
  }

  start(peerId: string): void {
    this.stopCadence(peerId);
    this.clearRenewal(peerId);
    this.activePeers.add(peerId);
    this.windowStartedAt.set(peerId, this.monotonicNowMs());
    this.sentAt.delete(peerId);
    this.pingCount.set(peerId, 0);
    this.sendPing(peerId);
    const timer = setInterval(() => {
      const offset = this.offsetTrackers.get(peerId);
      if (offset?.hostTimeBounds(this.nowMs(), this.nowMs())) {
        this.stopCadence(peerId);
        this.scheduleRenewal(peerId);
        return;
      }
      const count = this.pingCount.get(peerId) ?? 0;
      if (count >= this.maxPings) {
        this.stopCadence(peerId);
        this.scheduleRenewal(peerId);
        return;
      }
      this.sendPing(peerId);
    }, PING_INTERVAL_MS);
    this.timers.set(peerId, timer);
  }

  /** Tear down all probe state for a peer (connection destroy / clearPeerState). */
  stop(peerId: string): void {
    this.stopCadence(peerId);
    this.clearRenewal(peerId);
    this.activePeers.delete(peerId);
    this.windowStartedAt.delete(peerId);
    this.clockReadings.delete(peerId);
    this.invalidCurrent.delete(peerId);
    this.offsetTrackers.delete(peerId);
    this.calibratedTrackers.delete(peerId);
    this.sourceByPeer.delete(peerId);
    this.seqByPeer.delete(peerId);
    this.sentAt.delete(peerId);
    this.pingCount.delete(peerId);
    this.epochByPeer.delete(peerId);
  }

  /** Drop path-dependent clock confidence and reject delayed observations from the previous path. */
  resetForPath(peerId: string, pathEpoch: number): void {
    if (!Number.isSafeInteger(pathEpoch) || pathEpoch < (this.epochByPeer.get(peerId) ?? 0)) return;
    this.epochByPeer.set(peerId, pathEpoch);
    this.offsetTrackers.delete(peerId);
    this.sentAt.delete(peerId);
    this.sourceByPeer.delete(peerId);
    this.calibratedTrackers.get(peerId)?.resetForPath(pathEpoch);
    this.start(peerId);
  }

  onPong(peerId: string, pong: LatencyProbePong): void {
    const now = this.hostNow(peerId);
    const sentAt = this.sentAt.get(peerId)?.get(pong.probeId);
    if (sentAt === undefined || pong.hostWallMs !== sentAt) return;
    const offset = this.offsetTrackers.get(peerId) ?? new LatencyOffsetTracker();
    const wasTrusted = offset.isTrusted();
    offset.observeRoundTrip(pong.probeId, sentAt, now, pong.guestWallMs);
    if (wasTrusted && !offset.isTrusted()) this.invalidCurrent.add(peerId);
    this.offsetTrackers.set(peerId, offset);
    this.sentAt.get(peerId)?.delete(pong.probeId);
  }

  onReport(peerId: string, report: LatencyProbeReport): void {
    const pathEpoch = this.epochByPeer.get(peerId) ?? 0;
    if ((report.pathEpoch ?? 0) !== pathEpoch) return;
    // Estimates are log-only — do not feed the calibrated climb meter.
    if (report.source === 'estimate') {
      this.sourceByPeer.set(peerId, report.source);
      this.sinkCalibrated(peerId, {
        source: report.source,
        calibratedMs: null,
        rtpTimestamp: report.rtpTimestamp,
      });
      return;
    }

    const now = this.hostNow(peerId);
    const offset = this.offsetTrackers.get(peerId);
    const bounds = offset?.hostTimeBounds(report.guestDecodeWallMs, now);
    if (!offset || !bounds) { this.invalidCurrent.add(peerId); this.ensureClockConfidence(peerId); return; }
    if (bounds.earliestMs > now) return;
    const producerAgeMs = report.observationAgeMs ?? 0;
    if (!Number.isFinite(producerAgeMs) || producerAgeMs < 0) return;
    // Both intervals begin at the same frame observation. Take the conservative
    // maximum rather than adding producer age to the already complete transit age.
    const observationAgeMs = Math.max(producerAgeMs, now - bounds.earliestMs);
    const offsetMs = offset.offsetMs();
    if (offsetMs === null) return;

    const hostSendWallMs = this.deps.getHostSendWallMs?.(peerId, report.rtpTimestamp);
    if (hostSendWallMs === undefined) return;

    const raw = report.guestDecodeWallMs + offsetMs - hostSendWallMs;
    const calibratedMs = Math.min(Math.max(raw, 0), 10_000);
    const tracker = this.calibratedTrackers.get(peerId) ?? new CalibratedLatencyTracker({
      nowMs: this.monotonicNowMs,
    });
    const accepted = tracker.observe({
      rtpTimestamp: report.rtpTimestamp,
      hostSendWallMs,
      guestDecodeWallMs: report.guestDecodeWallMs,
      offsetMs,
      calibratedMs,
      observationSequence: report.observationSequence,
      observationAgeMs,
      pathEpoch: report.pathEpoch,
      timingSource: report.timingSource,
    });
    if (!accepted) return;
    this.invalidCurrent.delete(peerId);
    this.sourceByPeer.set(peerId, report.source);
    this.calibratedTrackers.set(peerId, tracker);
    this.sinkCalibrated(peerId, {
      source: report.source,
      calibratedMs,
      rtpTimestamp: report.rtpTimestamp,
    });
    if (tracker.snapshot().freshness.state === 'fresh') this.deps.onCalibratedSample?.(peerId, calibratedMs);
  }

  summary(peerId: string): CalibratedLatencySummary {
    const now = this.hostNow(peerId);
    const tracker = this.calibratedTrackers.get(peerId);
    const offset = this.offsetTrackers.get(peerId);
    const offsetTrusted = !!offset?.hostTimeBounds(now, now);
    if (!offsetTrusted) this.invalidCurrent.add(peerId);
    const currentTrusted = offsetTrusted && !this.invalidCurrent.has(peerId);
    const snapshot = tracker?.snapshot();
    return {
      lastMs: currentTrusted ? snapshot?.lastMs ?? null : null,
      p50Ms: tracker?.p50Ms() ?? null,
      p95Ms: tracker?.p95Ms() ?? null,
      floorMs: tracker?.floorMs() ?? null,
      excessMs: currentTrusted ? tracker?.excessMs() ?? null : null,
      sampleCount: tracker?.sampleCount() ?? 0,
      offsetTrusted,
      source: this.sourceByPeer.get(peerId) ?? null,
      freshness: currentTrusted ? snapshot?.freshness ?? { state: 'unavailable' } : { state: 'unavailable' },
      timingSource: snapshot?.timingSource ?? 'unavailable',
    };
  }

  /** Clock-correlated producer time for ordering only; never a latency estimate. */
  guestWallTimeBounds(peerId: string, guestWallMs: number, pathEpoch: number): { earliestMs: number; latestMs: number } | undefined {
    if (pathEpoch !== (this.epochByPeer.get(peerId) ?? 0)) return undefined;
    const now = this.hostNow(peerId);
    return this.offsetTrackers.get(peerId)?.hostTimeBounds(guestWallMs, now);
  }

  /** Recalibrate an expired clock before starting a resolution verification request. */
  ensureClockConfidence(peerId: string): void {
    const now = this.hostNow(peerId);
    if (this.offsetTrackers.get(peerId)?.hostTimeBounds(now, now)) return;
    if (!this.activePeers.has(peerId) || this.timers.has(peerId)) return;
    const elapsed = this.monotonicNowMs() - (this.windowStartedAt.get(peerId) ?? -Infinity);
    if (elapsed >= 0 && elapsed < CALIBRATION_RENEW_MS) return;
    this.offsetTrackers.delete(peerId);
    this.invalidCurrent.add(peerId);
    this.start(peerId);
  }

  private scheduleRenewal(peerId: string): void {
    this.clearRenewal(peerId);
    if (!this.activePeers.has(peerId)) return;
    this.renewals.set(peerId, setTimeout(() => {
      this.renewals.delete(peerId);
      this.offsetTrackers.delete(peerId);
      this.invalidCurrent.add(peerId);
      this.start(peerId);
    }, CALIBRATION_RENEW_MS));
  }

  private clearRenewal(peerId: string): void {
    const timer = this.renewals.get(peerId);
    if (timer) clearTimeout(timer);
    this.renewals.delete(peerId);
  }

  private hostNow(peerId: string): number {
    const wallMs = this.nowMs(), monotonicMs = this.monotonicNowMs();
    const previous = this.clockReadings.get(peerId);
    if (!Number.isFinite(wallMs) || !Number.isFinite(monotonicMs) || (previous &&
      (monotonicMs < previous.monotonicMs || Math.abs((wallMs - previous.wallMs) - (monotonicMs - previous.monotonicMs)) > 100))) {
      this.offsetTrackers.delete(peerId);
      this.invalidCurrent.add(peerId);
      this.sentAt.delete(peerId);
    }
    this.clockReadings.set(peerId, { wallMs, monotonicMs });
    return wallMs;
  }

  private sendPing(peerId: string): void {
    const seq = (this.seqByPeer.get(peerId) ?? 0) + 1;
    this.seqByPeer.set(peerId, seq);
    this.pingCount.set(peerId, (this.pingCount.get(peerId) ?? 0) + 1);
    let byProbe = this.sentAt.get(peerId);
    if (!byProbe) {
      byProbe = new Map();
      this.sentAt.set(peerId, byProbe);
    }
    const now = this.nowMs();
    byProbe.set(seq, now);
    const sent = this.deps.send(peerId, {
      type: LATENCY_PROBE_MESSAGE_TYPE,
      role: 'ping',
      probeId: seq,
      hostWallMs: now,
    });
    if (!sent) byProbe.delete(seq);
  }

  private stopCadence(peerId: string): void {
    const timer = this.timers.get(peerId);
    if (timer) {
      clearInterval(timer);
      this.timers.delete(peerId);
    }
  }

  private sinkCalibrated(
    peerId: string,
    payload: { source: LatencyProbeSource; calibratedMs: number | null; rtpTimestamp: number },
  ): void {
    const line = JSON.stringify({
      t: new Date(this.nowMs()).toISOString(),
      tag: '[CalibratedLatency]',
      peerId,
      ...payload,
    });
    this.deps.debugSink?.(line);
  }
}
